import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Badge } from "components/ui/badge";
import {
  Upload,
  X,
  File as FileIcon,
  Check,
  CalendarBlank,
  Plus,
  CheckCircle,
} from "@phosphor-icons/react";
import {
  AssetMaintenanceSchedule,
  MaintenanceFrequencyLabels,
  CompleteMaintenanceEventResponse,
} from "shared/types/asset";
import {
  useCompleteMaintenanceEventWithDetails,
  useUpdateNextDueDate,
  useCreateAdhocEvent,
} from "hooks/useAssets";
import { toast } from "react-toastify";

interface MaintenanceValidationDialogProps {
  schedule: AssetMaintenanceSchedule | null;
  onClose: () => void;
  onSuccess: () => void;
}

type DialogStep = "validate" | "post-validation";

export const MaintenanceValidationDialog = ({
  schedule,
  onClose,
  onSuccess,
}: MaintenanceValidationDialogProps) => {
  // Step 1 state
  const [summary, setSummary] = useState("");
  const [reportFile, setReportFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { completeEvent, isCompleting } =
    useCompleteMaintenanceEventWithDetails();

  // Step tracking
  const [step, setStep] = useState<DialogStep>("validate");
  const [_validationResponse, setValidationResponse] =
    useState<CompleteMaintenanceEventResponse | null>(null);

  // Step 2 state — next due date
  const [nextDueDate, setNextDueDate] = useState("");
  const [nextDueDateDirty, setNextDueDateDirty] = useState(false);
  const { updateNextDueDate, isUpdating: isUpdatingDate } =
    useUpdateNextDueDate();
  const [dateSaved, setDateSaved] = useState(false);

  // Step 2 state — ad-hoc event
  const [showAdhocForm, setShowAdhocForm] = useState(false);
  const [adhocDate, setAdhocDate] = useState("");
  const [adhocNotes, setAdhocNotes] = useState("");
  const { createAdhocEvent, isCreating: isCreatingAdhoc } =
    useCreateAdhocEvent();
  const [adhocCreated, setAdhocCreated] = useState(false);

  const isOpen = !!schedule;

  const resetState = () => {
    setSummary("");
    setReportFile(null);
    setStep("validate");
    setValidationResponse(null);
    setNextDueDate("");
    setNextDueDateDirty(false);
    setDateSaved(false);
    setShowAdhocForm(false);
    setAdhocDate("");
    setAdhocNotes("");
    setAdhocCreated(false);
  };

  const handleSubmit = async () => {
    if (!schedule) return;

    try {
      const response = await completeEvent(
        schedule.id,
        summary || undefined,
        reportFile || undefined,
      );

      toast.success("Onderhoud gevalideerd");

      // Transition to Step 2
      setValidationResponse(response);
      const proposedDate = response.proposedNextDueDate?.split("T")[0] || "";
      setNextDueDate(proposedDate);
      setStep("post-validation");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Kon onderhoud niet valideren";
      toast.error(message);
      console.error("Error completing maintenance event:", error);
    }
  };

  const handleSaveNextDueDate = async () => {
    if (!schedule || !nextDueDate) return;

    try {
      await updateNextDueDate(schedule.id, nextDueDate);
      toast.success("Volgende onderhoudsdatum bijgewerkt");
      setDateSaved(true);
      setNextDueDateDirty(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Kon datum niet bijwerken";
      toast.error(message);
      console.error("Error updating next due date:", error);
    }
  };

  const handleCreateAdhocEvent = async () => {
    if (!schedule || !adhocDate) return;

    try {
      await createAdhocEvent(
        schedule.id,
        adhocDate,
        adhocNotes || undefined,
      );
      toast.success("Extra onderhoudsmoment aangemaakt");
      setAdhocCreated(true);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Kon extra onderhoudsmoment niet aanmaken";
      toast.error(message);
      console.error("Error creating ad-hoc event:", error);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
    // If we completed validation, trigger refresh
    if (step === "post-validation") {
      onSuccess();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  const handleNextDueDateChange = (value: string) => {
    setNextDueDate(value);
    setNextDueDateDirty(true);
    setDateSaved(false);
  };

  if (!schedule) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === "validate"
              ? "Onderhoud valideren"
              : "Onderhoud gevalideerd"}
          </DialogTitle>
        </DialogHeader>

        {step === "validate" && (
          <>
            <div className="space-y-4">
              {/* Schedule info */}
              <div className="rounded-lg border p-3 bg-muted/30">
                <p className="text-sm font-medium">{schedule.name}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  <Badge variant="outline" className="text-xs">
                    {MaintenanceFrequencyLabels[schedule.frequency]}
                  </Badge>
                  {schedule.supplier && (
                    <Badge variant="outline" className="text-xs">
                      {schedule.supplier.name}
                    </Badge>
                  )}
                  {schedule.asset && (
                    <Badge variant="secondary" className="text-xs">
                      {schedule.asset.name || schedule.asset.id}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Summary textarea */}
              <div className="space-y-2">
                <Label htmlFor="summary">Onderhoudslog (optioneel)</Label>
                <Textarea
                  id="summary"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="Beschrijf het uitgevoerde onderhoud..."
                  rows={4}
                  maxLength={5000}
                />
              </div>

              {/* File upload */}
              <div className="space-y-2">
                <Label>Onderhoudsrapport (optioneel)</Label>
                {reportFile ? (
                  <div className="flex items-center gap-2 rounded-lg border p-3">
                    <FileIcon
                      size={20}
                      className="text-blue-600 shrink-0"
                    />
                    <span className="text-sm truncate flex-1">
                      {reportFile.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => {
                        setReportFile(null);
                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                      }}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
                  >
                    <Upload size={18} />
                    <span className="text-sm">
                      Klik om een bestand te uploaden
                    </span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setReportFile(file);
                  }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isCompleting}
              >
                Annuleren
              </Button>
              <Button onClick={handleSubmit} disabled={isCompleting}>
                <Check size={16} className="mr-1" />
                {isCompleting ? "Valideren..." : "Valideer"}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "post-validation" && (
          <>
            <div className="space-y-5">
              {/* Success message */}
              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3">
                <CheckCircle
                  size={20}
                  weight="fill"
                  className="text-green-600 shrink-0"
                />
                <p className="text-sm text-green-800">
                  Onderhoud succesvol gevalideerd
                </p>
              </div>

              {/* Next due date section */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <CalendarBlank size={16} />
                  Volgend periodiek onderhoud
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={nextDueDate}
                    onChange={(e) => handleNextDueDateChange(e.target.value)}
                    className="flex-1"
                  />
                  {nextDueDateDirty && (
                    <Button
                      size="sm"
                      onClick={handleSaveNextDueDate}
                      disabled={isUpdatingDate || !nextDueDate}
                    >
                      {isUpdatingDate ? "Opslaan..." : "Opslaan"}
                    </Button>
                  )}
                  {dateSaved && (
                    <CheckCircle
                      size={20}
                      weight="fill"
                      className="text-green-600 shrink-0"
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Pas de datum aan als het volgende onderhoud op een ander
                  moment moet plaatsvinden.
                </p>
              </div>

              {/* Ad-hoc event section */}
              <div className="space-y-3">
                {!showAdhocForm && !adhocCreated && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setShowAdhocForm(true)}
                  >
                    <Plus size={16} className="mr-1" />
                    Voeg een extra tussentijds onderhoudsmoment toe
                  </Button>
                )}

                {showAdhocForm && !adhocCreated && (
                  <div className="rounded-lg border p-3 space-y-3">
                    <p className="text-sm font-medium">
                      Extra onderhoudsmoment
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="adhoc-date">Datum</Label>
                      <Input
                        id="adhoc-date"
                        type="date"
                        value={adhocDate}
                        onChange={(e) => setAdhocDate(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="adhoc-notes">
                        Notitie (optioneel)
                      </Label>
                      <Textarea
                        id="adhoc-notes"
                        value={adhocNotes}
                        onChange={(e) => setAdhocNotes(e.target.value)}
                        placeholder="Bijv. hercontrole na gefaald onderhoud..."
                        rows={2}
                        maxLength={5000}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowAdhocForm(false);
                          setAdhocDate("");
                          setAdhocNotes("");
                        }}
                      >
                        Annuleren
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleCreateAdhocEvent}
                        disabled={isCreatingAdhoc || !adhocDate}
                      >
                        {isCreatingAdhoc
                          ? "Toevoegen..."
                          : "Toevoegen"}
                      </Button>
                    </div>
                  </div>
                )}

                {adhocCreated && (
                  <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3">
                    <CheckCircle
                      size={18}
                      weight="fill"
                      className="text-green-600 shrink-0"
                    />
                    <p className="text-sm text-green-800">
                      Extra onderhoudsmoment toegevoegd
                    </p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Sluiten</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
